import {
  ACCESSORY_KEYS,
  addItemQueryKeys,
  collectorSections,
  COLLECTOR_SCHEMAS,
  CONDITION_KEYS,
  isCollectorCategory,
  labelKey,
  optionKey,
  prefillFromItem,
  specifyKey,
  visibleFields,
  type Accessory,
  type CollectorValues,
  type ItemCondition,
} from "@kovernow/flows/add_item";
import { planRoute } from "@kovernow/flows/quote";
import { toRecoveryAction } from "@kovernow/flows/recovery";
import {
  canEditOrDelete,
  categoryLabelKey,
  detailAction,
  documentsOf,
  isLegacyItem,
  shouldFetchPolicy,
  toDisplayStatus,
  vaultQueryKeys,
  type VaultItem,
} from "@kovernow/flows/vault";
import { Button } from "@kovernow/react-ui/components/button";
import { DescriptionList, type DescriptionItem } from "@kovernow/react-ui/components/description-list";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@kovernow/react-ui/components/dropdown";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { StatusBadge } from "@kovernow/react-ui/components/status-badge";
import { useQuery } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { GatedButton } from "../../components/GatedButton";
import { Page } from "../../components/Page";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { DeleteItemDialog } from "./DeleteItemDialog";
import { ItemDocuments } from "./ItemDocuments";
import { useValidateGate, useValidateRecoveryHandlers, validateGateOptions } from "./validate-gate";
import {
  isNotFound,
  ItemNotFound,
  useItem,
  useItemFormatter,
  useItemName,
  useItemValue,
  type DetailNavState,
} from "./shared";

/**
 * W-6 item detail: StatusBadge, the pending/rejected banner, photos, name
 * and value, details, documents, and the one action the status offers.
 * Choose plan runs `POST /items/validate` first and maps its code to a
 * RecoveryView. Edit and Delete sit in the "More actions" menu while the
 * item has never had a policy. No pricing is fetched here.
 */
export function ItemDetail() {
  const t = useT();
  const { itemId = "" } = useParams();
  const { vaultApi, addItemApi, config, format } = useServices();
  const navigate = useNavigate();
  const location = useLocation();
  const nameOf = useItemName();
  const valueOf = useItemValue();
  const formatterFor = useItemFormatter();

  const itemQ = useItem(itemId);
  const item = itemQ.data;
  const fetchPolicy = !!item && shouldFetchPolicy(item, config.region);
  const policyQ = useQuery({
    queryKey: vaultQueryKeys.policy(item?.policyId ?? ""),
    queryFn: () => vaultApi.policy(item!.policyId!),
    enabled: fetchPolicy,
    retry: false,
  });
  // A failed policy fetch: no Extend action.
  const extensible = fetchPolicy && policyQ.data?.extensible === true;
  const account = useQuery({ queryKey: addItemQueryKeys.account(), queryFn: () => addItemApi.account() });
  const ownerId = account.data?.id ?? null;

  const [notice, setNotice] = useState<"item_has_policy" | null>(
    () => (location.state as DetailNavState | null)?.notice ?? null,
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const documentsHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const gate = useValidateGate(item, {
    extensible,
    onProceed: () => navigate(planRoute(itemId)),
  });
  const recoveryHandlers = useValidateRecoveryHandlers(itemId, {
    // W-7/W-8 bring the customer back to Choose plan, not here.
    verifyReturnTo: planRoute(itemId),
    focusDocuments: () => documentsHeadingRef.current?.focus(),
  });

  if (itemQ.isPending) {
    return (
      <Page titleKey="nav.itemDetail" flowKey="nav.vault">
        <p aria-busy="true" className="text-muted-foreground">
          {t("common.loading")}
        </p>
      </Page>
    );
  }
  if (!item) {
    if (isNotFound(itemQ.error)) return <ItemNotFound />;
    return (
      <Page titleKey="nav.itemDetail" flowKey="nav.vault">
        <div data-testid="item-error">
          <Recovery action={toRecoveryAction(itemQ.error)} onRetry={() => void itemQ.refetch()} retryable />
        </div>
      </Page>
    );
  }

  const name = nameOf(item);
  const editable = canEditOrDelete(item);
  const { collector, canEdit, manageable } = validateGateOptions(item, extensible);
  const display = toDisplayStatus(item.status);
  const action = detailAction(item, { extensible });
  const photos = documentsOf(item, "item");
  const value = valueOf(item);
  const editRoute = `/vault/${encodeURIComponent(item._id)}/edit`;

  const choosePlan = () => void gate.run();
  const validating = gate.busy;
  const validateView = gate.view;

  return (
    <Page titleKey="itemDetail.heading" titleValues={{ name }} headingKey="itemDetail.heading" headingValues={{ name }} flowKey="nav.vault">
      <div className="flex flex-col gap-6" data-testid="item-detail" data-status={item.status ?? ""}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col items-start gap-1.5">
            <StatusBadge status={display.status} label={t(`status.${display.status}`)} data-testid="item-status" />
            {display.claimNote ? <p className="text-sm text-muted-foreground">{t("vault.claimNote")}</p> : null}
          </div>
          {editable ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  ref={menuTriggerRef}
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("itemDetail.actions.moreActions", { name })}
                  className="size-11 shrink-0"
                  data-testid="item-menu"
                >
                  <MoreHorizontal aria-hidden="true" className="size-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit ? (
                  <DropdownMenuItem className="min-h-11" onSelect={() => navigate(editRoute)}>
                    {t("itemDetail.actions.edit")}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem className="min-h-11" onSelect={() => setDeleteOpen(true)}>
                  {t("itemDetail.actions.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <div role="status" aria-live="polite">
          {notice ? <InfoBanner tone="info" title={t(`errors.${notice}`)} data-testid="item-notice" /> : null}
        </div>

        {action.kind === "pending" ? (
          <InfoBanner tone="warning" title={t("itemDetail.pending.title")} body={t("itemDetail.pending.body")} data-testid="pending-banner" />
        ) : null}
        {action.kind === "rejected" ? (
          <InfoBanner
            tone="warning"
            title={t("itemDetail.rejected.title")}
            body={rejectionReason(item) ?? t("itemDetail.rejected.body")}
            data-testid="rejected-banner"
          />
        ) : null}

        <Photos photos={photos.map((p) => p.url)} name={name} />

        <div className="flex flex-col gap-1">
          {value ? (
            <p className="text-2xl font-semibold tabular-nums" data-testid="item-value">
              <span className="sr-only">{t("itemDetail.value")} </span>
              {value}
            </p>
          ) : null}
        </div>

        <section aria-labelledby="item-details-heading" className="flex flex-col gap-3">
          <h2 id="item-details-heading" className="text-xl font-semibold">
            {t("itemDetail.details.title")}
          </h2>
          <DescriptionList items={detailRows(item, t, format, formatterFor(item))} data-testid="item-details" />
        </section>

        <ItemDocuments
          item={item}
          name={name}
          collector={collector}
          manageable={manageable}
          ownerId={ownerId}
          headingRef={documentsHeadingRef}
        />

        <section aria-label={t("itemDetail.actions.choosePlan")} className="flex flex-col gap-3" data-testid="item-actions">
          {action.kind === "choose_plan" ? (
            <>
              <div className="sm:max-w-64">
                <GatedButton variant="cta" label={t("itemDetail.actions.choosePlan")} busy={validating} onClick={choosePlan} />
              </div>
              <p role="status" className="text-sm text-muted-foreground">
                {validating ? t("itemDetail.actions.checking") : ""}
              </p>
              {validateView?.kind === "recovery" ? (
                <div data-testid="validate-recovery" data-action={validateView.action.action}>
                  <Recovery
                    action={validateView.action}
                    message={validateView.message}
                    onRetry={choosePlan}
                    handlers={recoveryHandlers(validateView.target)}
                  />
                </div>
              ) : null}
            </>
          ) : null}
          {/* Outside the Choose plan block: an HK over-limit validate moves the item to Pending. */}
          {validateView?.kind === "info" ? (
            <div role="status" aria-live="polite">
              <InfoBanner tone="info" title={validateView.title} body={validateView.message} data-testid="validate-info" />
            </div>
          ) : null}
          {action.kind === "view_policy" ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button asChild variant="cta" size="touch">
                <Link to={`/policies/${encodeURIComponent(action.policyId)}`}>{t("itemDetail.actions.viewPolicy")}</Link>
              </Button>
              {action.extend ? (
                <Button asChild variant="outline" size="touch">
                  <Link to={`/policies/${encodeURIComponent(action.policyId)}/extend`}>{t("itemDetail.actions.extend")}</Link>
                </Button>
              ) : null}
            </div>
          ) : null}
          {action.kind === "rejected" ? (
            <Button asChild variant="outline" size="touch" className="self-start">
              <Link to="/account/support">{t("itemDetail.actions.contactSupport")}</Link>
            </Button>
          ) : null}
        </section>
      </div>

      {editable ? (
        <DeleteItemDialog
          item={item}
          name={name}
          ownerId={ownerId}
          open={deleteOpen}
          onOpenChange={(open) => {
            setDeleteOpen(open);
            if (!open) requestAnimationFrame(() => menuTriggerRef.current?.focus());
          }}
          onHasPolicy={() => {
            setNotice("item_has_policy");
            void itemQ.refetch();
          }}
        />
      ) : null}
    </Page>
  );
}

function rejectionReason(item: VaultItem): string | undefined {
  const r = item.attributes?.["underwriterReviewRejectionReason"];
  return typeof r === "string" && r.trim() ? r.trim() : undefined;
}

function Photos({ photos, name }: { photos: string[]; name: string }) {
  const t = useT();
  if (!photos.length) {
    return <p className="text-muted-foreground">{t("itemDetail.noPhoto")}</p>;
  }
  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="item-photos">
      {photos.map((url, i) => (
        <li key={url} className="aspect-square overflow-hidden rounded-md bg-muted">
          <img src={url} alt={t("itemDetail.photo", { n: i + 1, name })} className="size-full object-cover" />
        </li>
      ))}
    </ul>
  );
}

type T = ReturnType<typeof useT>;

/** Legacy: category, brand, model, type, description, purchase value and date, condition, accessories. Collector: the schema's visible fields. */
function detailRows(
  item: VaultItem,
  t: T,
  format: ReturnType<typeof useServices>["format"],
  /** Amounts in the item's own currency (as the headline value). */
  money: ReturnType<typeof useServices>["format"],
): DescriptionItem[] {
  const rows: DescriptionItem[] = [{ id: "category", term: t("itemDetail.details.category"), description: t(categoryLabelKey(item.classification)) }];
  const category = item.classification;
  if (isCollectorCategory(category)) {
    const values = prefillFromItem(category, { _id: item._id, attributes: item.attributes, purchasedAt: item.purchasedAt });
    for (const section of collectorSections(category)) {
      if (section === "documents") continue;
      for (const f of visibleFields(category, section, values)) {
        if (f.type === "upload") continue;
        const text = collectorValueText(category, f.key, values, t, money);
        if (!text) continue;
        rows.push({ id: f.key, term: t(labelKey(category, f.key)), description: text });
      }
    }
    return rows;
  }
  const push = (id: string, term: string, v: string | undefined | null) => {
    if (v && v.trim()) rows.push({ id, term, description: v });
  };
  push("brand", t("itemDetail.details.brand"), item.brand);
  push("model", t("itemDetail.details.model"), item.model);
  push("type", t("itemDetail.details.type"), item.type);
  push("description", t("itemDetail.details.description"), item.isBrand === false ? item.notes : undefined);
  if (typeof item.valueAmount === "number") {
    rows.push({ id: "purchaseValue", term: t("itemDetail.details.purchaseValue"), description: money.currency(item.valueAmount) });
  }
  if (item.purchasedAt && !Number.isNaN(new Date(item.purchasedAt).getTime())) {
    rows.push({ id: "purchaseDate", term: t("itemDetail.details.purchaseDate"), description: format.date(item.purchasedAt) });
  }
  if (item.condition) {
    const key = CONDITION_KEYS[item.condition as ItemCondition];
    rows.push({ id: "condition", term: t("itemDetail.details.condition"), description: key ? t(key) : item.condition });
  }
  if (isLegacyItem(item)) {
    const acc = (item.listAccessory ?? []).map((a) => (ACCESSORY_KEYS[a as Accessory] ? t(ACCESSORY_KEYS[a as Accessory]) : a));
    rows.push({
      id: "accessories",
      term: t("itemDetail.details.accessories"),
      description: acc.length ? acc.join(", ") : t("itemDetail.details.none"),
      muted: !acc.length,
    });
  }
  return rows;
}

function collectorValueText(
  category: Parameters<typeof labelKey>[0],
  key: string,
  values: CollectorValues,
  t: T,
  format: ReturnType<typeof useServices>["format"],
): string | undefined {
  const field = COLLECTOR_SCHEMAS[category].sections.flatMap((s) => s.fields).find((f) => f.key === key);
  if (!field) return undefined;
  const raw = values[key];
  if (field.type === "total") return undefined;
  if (raw === undefined || raw === "" || (Array.isArray(raw) && !raw.length)) return undefined;
  const optionText = (v: string) => {
    const opt = field.options?.find((o) => o.value === v);
    if (!opt) return v;
    const label = t(optionKey(category, field, v));
    const spec = opt.specify ? values[specifyKey(key)] : undefined;
    return typeof spec === "string" && spec.trim() && !field.specifyCents ? `${label}: ${spec.trim()}` : label;
  };
  if (field.type === "currency") {
    const n = Number(raw);
    return Number.isFinite(n) ? format.currency(n) : undefined;
  }
  if (field.type === "date") return typeof raw === "string" ? format.date(`${raw}T00:00:00Z`) : undefined;
  if ((field.type === "grade" || field.type === "number") && typeof raw === "string" && Number.isFinite(Number(raw))) {
    return format.number(Number(raw));
  }
  if (Array.isArray(raw)) return raw.map(optionText).join(", ");
  if (field.options?.length) {
    const opt = field.options.find((o) => o.value === raw);
    if (opt?.specify && field.specifyCents) {
      const amount = Number(values[specifyKey(key)]);
      return Number.isFinite(amount) && amount > 0 ? `${t(optionKey(category, field, raw))}: ${format.currency(amount)}` : t(optionKey(category, field, raw));
    }
    return optionText(raw);
  }
  return raw;
}
